import { AlertTriangle, X } from 'lucide-react';
import { Link } from 'react-router';

import { useCurrentUser, useUsageNudge } from '@chartai/api-client';
import { PLAN_LABELS } from '@chartai/utils';

import { UPGRADE_ROUTE } from '@/shared/utils/upgradeRoute';

/**
 * CAB-606 — warns Free and Basic users as they approach their monthly minutes,
 * so hitting the cap is never a surprise.
 *
 * Renders nothing unless the nudge applies. Everything that decides that lives
 * in `useUsageNudge` / `shouldNudgeUpgrade`, which is also where the rule that
 * this must never coexist with the at-100% blocked state is enforced and
 * tested.
 *
 * Dismissal is per billing period, so it returns next month rather than being
 * silenced forever by one click.
 */
export function UsageNudgeBanner() {
  const { user } = useCurrentUser();
  const { isVisible, nextPlan, usedMinutes, quotaMinutes, dismiss } = useUsageNudge(
    user?.uid ?? null,
  );

  if (!isVisible || quotaMinutes === null) {
    return null;
  }

  const remaining = Math.max(0, quotaMinutes - usedMinutes);

  return (
    <div
      role="status"
      className="border-border mb-4 flex items-center gap-3 rounded-xl border bg-amber-50 px-4 py-3"
    >
      <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />

      <div className="min-w-0 flex-1">
        <p className="text-foreground text-sm font-medium">
          {remaining} of your {quotaMinutes} monthly minutes remaining
        </p>
        {nextPlan !== null && (
          <p className="text-muted-foreground mt-0.5 text-sm">
            Upgrade to {PLAN_LABELS[nextPlan]} for more recording time.
          </p>
        )}
      </div>

      <Link
        to={UPGRADE_ROUTE}
        className="bg-primary shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium text-white"
      >
        View plans
      </Link>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-muted-foreground hover:text-foreground shrink-0 rounded p-1"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
